import React, { useEffect, useState } from 'react';
import { PictureFilled } from '@ant-design/icons';
import { Upload } from 'antd';
import PropTypes from 'prop-types';
import BraftEditor from 'braft-editor';
import classnames from 'classnames';
import { ContentUtils } from 'braft-utils';

import Tag from './component/tag';
import ReplaceWord from './component/replace-word';
import { setData } from './store/index';
import useUploadImage from '../../hooks/use-upload-image';

import style from './index.less';
import { getEmitter } from '@laigu/utils';

const defaultControls = [
  'font-size',
  'text-color',
  'bold',
  'italic',
  'underline',
  'remove-styles',
  'text-indent',
  'text-align',
  'list-ul',
  'list-ol',
  'link',
  'clear'
];

const imageControls = [
  'float-left', // 设置图片左浮动
  'float-right', // 设置图片右浮动
  'align-left', // 设置图片居左
  'align-center', // 设置图片居中
  'align-right', // 设置图片居右
  'link', // 设置图片超链接
  'size', // 设置图片尺寸
  'remove' // 删除图片
];

const mediaSetting = {
  accepts: {
    video: false,
    audio: false
  },
  externals: false
};
const defaultHtmlContent = '';
// https://github.com/margox/braft-editor/issues/569
// 不支持卸载某项功能  用id的方式做显隐控制
const tagIncludeEditorId = ['guide-editor'];
const replaceWordEditorId = ['replace-word'];
const replaceIvrEditorId = ['replace-ivr'];
const replaceAttrEditorId = ['replace-attr'];
BraftEditor.use([
  Tag({
    includeEditors: tagIncludeEditorId
  }),
  ReplaceWord({
    includeEditors: replaceWordEditorId
  })
]);

const addEditorIdList = [...tagIncludeEditorId, ...replaceWordEditorId, ...replaceIvrEditorId, ...replaceAttrEditorId];

const emojiList = [
  '🤣',
  '🙌',
  '💚',
  '💛',
  '👏',
  '😉',
  '💯',
  '💕',
  '💞',
  '💘',
  '💙',
  '💝',
  '🖤',
  '💜',
  '❤️',
  '😍',
  '😻',
  '💓',
  '💗',
  '😋',
  '😇',
  '😂',
  '😹',
  '😘',
  '💖',
  '😁',
  '😀',
  '🤞',
  '😲',
  '😄',
  '😊',
  '👍',
  '😌',
  '😃',
  '😅',
  '✌️',
  '🤗',
  '💋',
  '😗',
  '😽',
  '😚',
  '🤠',
  '😙',
  '😺',
  '👄',
  '😸',
  '😏',
  '😼',
  '👌',
  '😎',
  '😆',
  '😛',
  '🙏',
  '🤝',
  '🙂',
  '🤑',
  '😝',
  '😐',
  '😑',
  '🤤',
  '😤',
  '🙃',
  '🤡',
  '😶',
  '😪',
  '😴',
  '😵',
  '😓',
  '👊',
  '😦',
  '😷',
  '😜',
  '🤓',
  '👻'
];

const emitter = getEmitter();

// 部分情况下重新渲染会导致自动聚焦，添加memo减伤不必要的渲染
// 不可以加载export处，会导致Editor.BraftEditor不可用
const Editor = React.memo(props => {
  const {
    id,
    defaultHtml,
    className,
    extendControls,
    onChange,
    value,
    contentClassName,
    removeImageIcon,
    keywordOptions
  } = props;
  const editorStateParams = addEditorIdList.includes(id) ? { editorId: id } : {};
  const [editorState, setEditorState] = useState(BraftEditor.createEditorState(defaultHtmlContent, editorStateParams));
  const { handleUploadImage } = useUploadImage();

  const uploadHandler = param => {
    if (!param.file) {
      return;
    }
    const formData = new window.FormData();
    formData.append('file', param.file);
    handleUploadImage({ file: param.file, type: 'widget' }).then(({ data: res }) => {
      const photo_url = res.photo_url || (res.data && res.data.photo_url);
      onChange(
        ContentUtils.insertMedias(props.value, [
          {
            type: 'IMAGE',
            url: photo_url
          }
        ])
      );
    });
  };

  const extendControlsWithUpload = [
    ...extendControls,
    {
      key: 'antd-uploader',
      type: 'component',
      title: '上传图片',
      component: (
        <Upload accept='image/*' showUploadList={false} customRequest={uploadHandler}>
          <button type='button' className='control-item button upload-button' data-title='插入图片'>
            <PictureFilled />
          </button>
        </Upload>
      )
    }
  ];

  useEffect(() => {
    if (typeof defaultHtml !== 'undefined') {
      setEditorState(BraftEditor.createEditorState(defaultHtml, editorStateParams));
      if (onChange) {
        onChange(BraftEditor.createEditorState(defaultHtml));
      }
    }
  }, [defaultHtml]);

  useEffect(() => {
    if (keywordOptions?.visit) {
      const options = { replaceKeywordOptions: keywordOptions };
      emitter.emit('setEditorConfig', options);
      setData(options);
    } else if (keywordOptions?.varList) {
      const options = { varList: keywordOptions.varList };
      setData(options);
    }
  }, [keywordOptions]);

  return (
    <BraftEditor
      {...props}
      handlePastedFiles={files => uploadHandler({ file: files[0] })}
      imageControls={imageControls}
      value={value || editorState}
      emojis={emojiList}
      imageResizable={false}
      media={mediaSetting}
      extendControls={removeImageIcon ? extendControls : extendControlsWithUpload}
      contentClassName={classnames(contentClassName, style.editor_content)}
      className={`${className} ${style.editor}`}
      language="zh"
    />
  );
});

Editor.propTypes = {
  ...BraftEditor.propTypes,
  extendControls: PropTypes.array,
  controls: PropTypes.array,
  className: PropTypes.string
};

Editor.defaultProps = {
  ...BraftEditor.defaultProps,
  extendControls: [],
  controls: defaultControls,
  className: ''
};

Editor.BraftEditor = BraftEditor;

function getPureTextFromHTML(htmlStr = '') {
  return htmlStr
    .replace(/<img[^>]*?>/g, '[图片]')
    .replace(/&nbsp;/g, ' ')
    .replace(/<\/?.+?>/g, '')
    .replace(/ /g, '');
}

function getPureTextFromHTMLWithBlank(htmlStr = '') {
  return htmlStr
    .replace(/<img[^>]*?>/g, '[图片]')
    .replace(/&nbsp;/g, ' ')
    .replace(/<\/?.+?>/g, '');
}

Editor.getPureTextFromHTML = getPureTextFromHTML;
Editor.getPureTextFromHTMLWithBlank = getPureTextFromHTMLWithBlank;

export { getPureTextFromHTML, getPureTextFromHTMLWithBlank };

export default Editor;
